import { Injectable } from '@angular/core';
import { BaryAddressUtil } from '../app/shared/util/bary-address-util';
import { IPricingTransporterConfiguration } from '../models/pricing-transporter.configuration';
import { FormBuilder, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { PricingTransporterConfigurationService } from './pricing-transporter-configuration.service';
import { take } from 'rxjs/operators';
import { mapAsBody } from '../app/shared/util/request-util';
import { BaryFormBuilder } from '../models/forms/builder/BaryFormBuilder';
import { TransportItem } from '../models/transport-item.model';
import { GenericBaryFormGroup, IGenericBaryFormGroup } from '../models/forms/GenericBaryFormGroup';
import { TransportItemControls, TransportItemFormBuilder } from '../models/forms/builder/TransportItemFormBuilder';
import { TransportControls, TransportFormBuilder } from '../models/forms/builder/TransportFormBuilder';
import { SearchPricingControls, SearchPricingFormBuilder } from '../models/forms/builder/SearchPricingFormBuilder';
import { SearchPricingFormGroup } from '../models/forms/SearchPricingFormGroup';
import { UserTypeEnum } from '../models/enums/user-type.enum';
import { BaryUserControls, BaryUserFormBuilder, RestrictedBaryUserControls } from '../models/forms/builder/BaryUserFormBuilder';
import { Transporter } from '../models/transporter.model';
import { Transport } from '../models/transport.model';
import { TransportFormGroup } from '../models/forms/TransportFormGroup';
import { AddressControls, AddressFormBuilder, AddressOption } from '../models/forms/builder/AddressFormBuilder';
import { AddressFormGroup } from '../models/forms/AddressFormGroup';
import { BillAddressControls, BillAddressFormBuilder, BillAddressOption } from '../models/forms/builder/BillAddressFormBuilder';
import { BillAddressFormGroup } from '../models/forms/BillAddressFormGroup';
import { CompanyControls, CompanyFormBuilder } from '../models/forms/builder/CompanyFormBuilder';
import { AssociationControls, AssociationFormBuilder } from '../models/forms/builder/AssociationFormBuilder';
import { LoginControls, LoginFormBuilder } from '../models/forms/builder/LoginFormBuilder';
import { NewShopControls, NewShopFormGroupModel } from '../models/new-shop-form-group.model';
import { OfferControls, OfferFormGroup } from '../models/offer-form-group.model';
import { OfferFormGroupBuilder } from '../models/offer-form-group-builder';
import { IOfferHandlingType } from '../models/offer-handling-type.model';
import { OfferHandlingTypeControls, OfferHandlingTypeFormBuilder } from '../models/forms/builder/OfferHandlingTypeBuilder';
import { ICustomer } from '../models/customer.model';
import { ContactControls, ContactFormBuilder } from '../models/forms/builder/ContactFormBuilder';
import { MiddleCustomerControls } from '../app/shared/ui/cards/customer-card/customer-card.component';
import { MiddleUserForcedActivationControls } from '../app/shared/ui';
import { HeadbandControls, IHeadband } from '../models/headband.model';
import { ThemeView } from '../models/theme-view.model';
import { ThemeData } from '../models/theme-data.model';
import { ThemeFormGroup } from '../models/forms/ThemeFormGroup';
import { ITransportExtraCondition } from '../models/transport-extra-condition.model';
import { TransportExtraConditionControls } from '../models/transport-extra-condition.controls';
import { ThemeDataFormGroup } from '../models/forms/ThemeDataFormGroup';
import { ThemeControls, ThemeFormBuilder } from '../models/forms/builder/ThemeFormBuilder';
import { MiddleCommentControls } from '../app/shared/ui/cards/comment-card/comment-card.component';
import { RegisteredAddressControls, RegisteredAddressFormGroup } from '../models/forms/RegisteredAddressFormGroup';
import { IRegisteredAddress } from '../models/registered-address.model';
import { RegisteredAddressFormBuilder } from '../models/forms/builder/RegisteredAddressFormBuilder';
import { ThemeDataControls, ThemeDataFormBuilder } from '../models/forms/builder/ThemeDataFormBuilder';
import { CarouselItem } from '../models/carousel-item.model';
import { CarouselItemControls, CarouselItemFormBuilder } from '../models/forms/builder/CarouselItemFormBuilder';
import { FaqItem } from '../models/faq-item';
import { FaqItemControls, FaqItemFormBuilder } from '../models/forms/builder/FaqItemFormBuilder';
import { StringUtil } from '../app/shared/util/string-util';
import { CIVILITIES_OPTIONS } from '../models/enums/civilities-option.enum';
import { PasswordStrengthValidator } from '../validators/password-strength.validator';
import { BaryValidator } from '../validators/bary.validator';
import { HandlingTypeEnum } from '../models/enums/handling-type.enum';
import { VALIDATOR_CONSTANTS } from '../models/constants/validator.const';
import { ItemCondition } from '../models/enums/item-condition.enum';
import { OfferStatus } from '../models/enums/offer-status.enum';
import { PricingUnitEnum } from '../models/enums/pricing-unit.enum';
import { PricingItemScope } from '../models/enums/pricing-item-scope.enum';
import { IContact } from '../models/contact.model';
import { CalculationMethod } from '../models/enums/calculation-method.enum';
import { AddressTypeEnum } from '../models/enums/address-type.enum';
import { RegisteredAddressMapper } from '../app/shared/mapper/registered-address-mapper';
import { IOfferDescriptionItem, OfferDescriptionItemControls } from '../models/offer-description-item.model';
import { IPricingOption } from '../models/pricing-option.model';
import { TransportStep } from '../models/enums/transport-step.enum';
import { InsuranceItem, InsuranceItemControls } from '../models/insurance-item.model';
import { Merchandise, MerchandiseControls } from '../models/merchandise.model';
import { PricingOptionControls } from '../app/entities/offer/modification/offer-modification-options/offer-modification-pricing-option/offer-modification-pricing-option.component';
import { OfferViewDTO } from '../models/offer-view-dto.type';
import { PhoneValidator } from '../validators/phone.validator';
import { OfferMerchandiseType } from '../models/enums/offer-merchandise-type.enum';
import { ServiceTypeEnum } from '../models/enums/offer-service-type.enum';
import { MeasureUnitEnum } from '../models/vehicle.model';
import { User } from '../models/user.model';
import { LIST_PRICING_UNIT } from '../models/measure-unit.model';

@Injectable({
  providedIn: 'root',
})
export class BaryFormFactoryService {
  pricingTransporterConfiguration?: IPricingTransporterConfiguration;

  constructor(protected fb: FormBuilder, protected baryAddressUtil: BaryAddressUtil, protected pricingTransporterConfigurationService: PricingTransporterConfigurationService) {
    this.pricingTransporterConfigurationService
      .retrieveConfiguration()
      .pipe(take(1))
      .pipe(mapAsBody)
      .subscribe(config => {
        this.pricingTransporterConfiguration = config;
      });
  }

  baryFormBuilder(): BaryFormBuilder {
    return new BaryFormBuilder(this).init();
  }

  makeTransportItemForm(transportItem?: TransportItem): IGenericBaryFormGroup<TransportItemControls> {
    return new TransportItemFormBuilder(this).init(transportItem).build();
  }

  makeTransportForm(transporter: Transporter, transport?: Transport): TransportFormGroup {
    return new TransportFormBuilder(this).init(transporter, transport).build();
  }

  makeSearchPricingForm(): SearchPricingFormGroup {
    return new SearchPricingFormBuilder(this).init().build();
  }

  makeRegistrationUserForm(baryUserType: UserTypeEnum = UserTypeEnum.COMPANY): IGenericBaryFormGroup<BaryUserControls> {
    return new BaryUserFormBuilder(this).init(baryUserType).build() as IGenericBaryFormGroup<BaryUserControls>;
  }

  makeRestrictedBaryUserForm(baryUserType: UserTypeEnum = UserTypeEnum.COMPANY): IGenericBaryFormGroup<RestrictedBaryUserControls> {
    return new BaryUserFormBuilder(this).init(baryUserType).isRestricted().build() as IGenericBaryFormGroup<RestrictedBaryUserControls>;
  }

  makeAddressForm(option?: AddressOption): AddressFormGroup {
    return new AddressFormBuilder(this, this.baryAddressUtil).init(option).build();
  }

  makeBillAddressForm(option?: BillAddressOption): BillAddressFormGroup {
    return new BillAddressFormBuilder(this, this.baryAddressUtil).init(option).build();
  }

  makeCompanyForm(): IGenericBaryFormGroup<CompanyControls> {
    return new CompanyFormBuilder(this).init().build();
  }

  makeAssociationForm(): IGenericBaryFormGroup<AssociationControls> {
    return new AssociationFormBuilder(this).init().build();
  }

  makeLoginForm(): IGenericBaryFormGroup<LoginControls> {
    return new LoginFormBuilder(this).init().build();
  }

  makeNewShopForm(): NewShopFormGroupModel {
    return new NewShopFormGroupModel('newShopForm', this.makeNewShopControls());
  }

  public makeOfferForm(offer: OfferViewDTO): OfferFormGroup {
    return new OfferFormGroupBuilder(this).init(offer).build();
  }

  makeOfferHandlingTypeForm(offerHandlingType?: IOfferHandlingType): IGenericBaryFormGroup<OfferHandlingTypeControls> {
    return new OfferHandlingTypeFormBuilder(this).initOfferHandlingType(offerHandlingType).build();
  }

  makeRegisteredAddressForm(user: User, registeredAddress?: IRegisteredAddress): RegisteredAddressFormGroup {
    return new RegisteredAddressFormBuilder(this).init(user, registeredAddress).build();
  }

  makeContactForm(contact?: IContact): IGenericBaryFormGroup<ContactControls> {
    return new ContactFormBuilder(this).initContact(contact).build();
  }

  makeMiddleCustomerForm(customer?: ICustomer): IGenericBaryFormGroup<MiddleCustomerControls> {
    return new GenericBaryFormGroup('customerForm', this.makeMiddleCustomerControls(customer));
  }

  makeMiddleCommentForm(customer?: ICustomer): IGenericBaryFormGroup<MiddleCommentControls> {
    return new GenericBaryFormGroup('commentForm', this.makeMiddleCommentControls(customer));
  }

  makeUserMiddleForcedActivation(customer?: ICustomer): IGenericBaryFormGroup<MiddleUserForcedActivationControls> {
    return new GenericBaryFormGroup('forcedActivationForm', this.makeMiddleUserForcedActivationControls(customer));
  }

  makeHeadbandForm(headband?: IHeadband): IGenericBaryFormGroup<HeadbandControls> {
    return new GenericBaryFormGroup('headbandForm', this.makeHeadbandControls(headband));
  }

  makeTransportExtraConditionsForm(isAdr = false, transportExtraCondition?: ITransportExtraCondition): IGenericBaryFormGroup<TransportExtraConditionControls> {
    return new GenericBaryFormGroup('transportExtraConditionForm', this.makeTransportExtraConditionsControls(isAdr, transportExtraCondition));
  }

  makeThemeForm(themeView?: ThemeView): ThemeFormGroup {
    return new ThemeFormBuilder(this).init(themeView).build();
  }

  makeThemeDataForm(themeData?: ThemeData): ThemeDataFormGroup {
    return new ThemeDataFormBuilder(this).init(themeData).build();
  }

  makeCarouselItemForm(carouselItem?: CarouselItem): IGenericBaryFormGroup<CarouselItemControls> {
    return new CarouselItemFormBuilder(this).init(carouselItem).build();
  }

  makeFaqItemForm(faqItem: FaqItem): IGenericBaryFormGroup<FaqItemControls> {
    return new FaqItemFormBuilder(this).init(faqItem).build();
  }

  makeAddressWithOptionControls(option?: AddressOption): AddressControls {
    return {
      autofill: this.fb.control(option?.address ? this.baryAddressUtil.updateGmapsAddressString(option.address) : '', option?.withoutValidators ? [] : [Validators.required]),
      fullname: this.fb.control(
        option?.address?.fullname ?? '',
        option?.withoutValidators || option?.withoutFullname ? [] : [Validators.required, Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]
      ),
      streetNumber: this.fb.control(option?.address?.streetNumber ?? '', [Validators.maxLength(10), Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      street: this.fb.control(
        option?.address?.street ?? '',
        option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]
      ),
      city: this.fb.control(
        option?.address?.city ?? '',
        option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]
      ),
      cityCode: this.fb.control(option?.address?.cityCode ?? '', [Validators.maxLength(10)]),
      country: this.fb.control(option?.address?.country ?? '', option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(70)]),
      zipCode: this.fb.control(option?.address?.zipCode ?? '', option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(10)]),
      floor: this.fb.control(option?.address?.floor ?? undefined),
      additionals: this.fb.control(option?.address?.additionals ?? '', [Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      gps_coords: this.fb.control(option?.address?.gps_coords ?? '', []),
    };
  }

  makeBillAddressWithOptionControls(option?: BillAddressOption): BillAddressControls {
    return {
      autofill: this.fb.control(option?.address ? this.baryAddressUtil.updateGmapsAddressString(option.address) : '', option?.withoutValidators ? [] : [Validators.required]),
      fullname: this.fb.control(
        option?.address?.fullname ?? '',
        option?.withoutValidators || option?.withoutFullname ? [] : [Validators.required, Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]
      ),
      streetNumber: this.fb.control(option?.address?.streetNumber ?? '', [Validators.maxLength(10), Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      street: this.fb.control(
        option?.address?.street ?? '',
        option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]
      ),
      city: this.fb.control(
        option?.address?.city ?? '',
        option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]
      ),
      cityCode: this.fb.control(option?.address?.cityCode ?? '', [Validators.maxLength(10)]),
      country: this.fb.control(option?.address?.country ?? '', option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(70)]),
      zipCode: this.fb.control(option?.address?.zipCode ?? '', option?.withoutValidators ? [] : [Validators.required, Validators.maxLength(10)]),
      floor: this.fb.control(option?.address?.floor ?? undefined),
      additionals: this.fb.control(option?.address?.additionals ?? '', [Validators.maxLength(70), Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      billEmail: this.fb.control(option?.billEmail ?? '', [Validators.minLength(5), Validators.maxLength(70), Validators.email]),
    };
  }

  makeCompanyControls(): {
    companyName: FormControl;
    companyLegalStatus: FormControl;
    siret: FormControl;
    vatNumber: FormControl;
  } {
    return {
      companyName: this.fb.control('', [Validators.required, Validators.minLength(3), Validators.maxLength(254), Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      siret: this.fb.control('', [Validators.required, Validators.pattern('^[0-9]{14}')]),
      vatNumber: this.fb.control(undefined),
      companyLegalStatus: this.fb.control(undefined, [Validators.required]),
    };
  }

  makeBaryUserControls(userType: UserTypeEnum = UserTypeEnum.INDIVIDUAL): BaryUserControls {
    const controls = {
      civility: this.fb.control(CIVILITIES_OPTIONS[0].value, [Validators.required]),
      firstName: this.fb.control('', [Validators.required, Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      lastName: this.fb.control('', [Validators.required, Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      phone: this.fb.control('', [Validators.required, PhoneValidator]),
      email: this.fb.control('', [Validators.required, Validators.minLength(5), Validators.maxLength(70), Validators.email]),
      password: this.fb.control('', [Validators.required, Validators.minLength(6), Validators.maxLength(50), PasswordStrengthValidator]),
      confirmPassword: this.fb.control('', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]),
      userType: this.fb.control(userType),
      cgv: this.fb.control(false, [BaryValidator.requireTrueOrError('register.messages.error.cgvRequire')]),
      dateBirth: this.fb.control(undefined, [Validators.required]),
      isTemporalPassword: this.fb.control(false),
      position: this.fb.control(undefined, [Validators.required]),
    };

    if (userType === UserTypeEnum.INDIVIDUAL) {
      controls.position = this.fb.control(undefined, []);
    }
    return controls;
  }

  makeAssociationControls(): {
    companyName: FormControl;
    companyLegalStatus: FormControl;
    siret: FormControl;
    vatNumber: FormControl;
  } {
    return {
      companyName: this.fb.control('', [Validators.required, Validators.minLength(3), Validators.maxLength(254), Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      siret: this.fb.control('', [Validators.pattern('^[0-9]{14}')]),
      vatNumber: this.fb.control('', [Validators.minLength(13), Validators.maxLength(13)]),
      companyLegalStatus: this.fb.control(undefined),
    };
  }

  makePickupContactControls(transport?: Transport): ContactControls {
    const phoneValidators: ValidatorFn[] = [PhoneValidator];

    if (this.pricingTransporterConfiguration?.customerPhoneRequired) {
      phoneValidators.push(Validators.required);
    }

    return {
      civility: this.fb.control(transport?.pickupContactCivility ?? CIVILITIES_OPTIONS[0].value, []),
      firstName: this.fb.control(transport?.pickupContactFirstName ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      lastName: this.fb.control(transport?.pickupContactLastName ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      mail: this.fb.control(transport?.pickupContactMail ?? '', [Validators.email]),
      phone: this.fb.control(transport?.pickupContactPhone ?? '', phoneValidators),
    };
  }

  makeDeliveryContactControls(transport?: Transport): ContactControls {
    const phoneValidators: ValidatorFn[] = [PhoneValidator];

    if (this.pricingTransporterConfiguration?.customerPhoneRequired) {
      phoneValidators.push(Validators.required);
    }

    return {
      civility: this.fb.control(transport?.deliveryContactCivility ?? CIVILITIES_OPTIONS[0].value, []),
      firstName: this.fb.control(transport?.deliveryContactFirstName ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      lastName: this.fb.control(transport?.deliveryContactLastName ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      mail: this.fb.control(transport?.deliveryContactMail ?? '', [Validators.email]),
      phone: this.fb.control(transport?.deliveryContactPhone ?? '', phoneValidators),
    };
  }

  public makeTransportItemControls(transportItem?: TransportItem): TransportItemControls {
    const serialize = transportItem ? transportItem.serialize() : {};
    const transportItemControls = Object.create({});
    transportItemControls.handlingType = new FormControl(serialize?.handlingType || HandlingTypeEnum.PARCEL, [Validators.required]);

    transportItemControls.dimension = new FormControl(serialize?.dimension);
    transportItemControls.maxHeight = new FormControl({ value: serialize?.maxHeight, disabled: serialize?.handlingType === HandlingTypeEnum.CRATE_OF_WINE }, [
      Validators.required,
      BaryValidator.minForPalletAndParcel(transportItemControls.handlingType),
      BaryValidator.maxForPalletAndParcel(
        transportItemControls.handlingType,
        this.pricingTransporterConfiguration?.quotationItemsPalletMaxHeight,
        this.pricingTransporterConfiguration?.quotationItemsParcelMaxHeight
      ),
    ]);
    transportItemControls.length = new FormControl({ value: serialize?.length, disabled: serialize?.handlingType === HandlingTypeEnum.CRATE_OF_WINE }, [
      Validators.required,
      Validators.min(1),
      BaryValidator.maxForPalletAndParcel(
        transportItemControls.handlingType,
        this.pricingTransporterConfiguration?.quotationItemsPalletMaxLength,
        this.pricingTransporterConfiguration?.quotationItemsParcelMaxLength
      ),
    ]);
    transportItemControls.quantity = new FormControl(serialize?.quantity, [
      Validators.required,
      Validators.min(1),
      BaryValidator.maxForPalletAndParcel(
        transportItemControls.handlingType,
        this.pricingTransporterConfiguration?.quotationItemsPalletMaxQuantity,
        this.pricingTransporterConfiguration?.quotationItemsParcelMaxQuantity
      ),
    ]);
    transportItemControls.weight = new FormControl({ value: serialize?.weight, disabled: serialize?.handlingType === HandlingTypeEnum.CRATE_OF_WINE }, [
      Validators.required,
      Validators.min(0.1),
      BaryValidator.maxForPalletAndParcel(
        transportItemControls.handlingType,
        this.pricingTransporterConfiguration?.quotationItemsPalletMaxWeight,
        this.pricingTransporterConfiguration?.quotationItemsParcelMaxWeight
      ),
    ]);
    transportItemControls.width = new FormControl({ value: serialize?.width, disabled: serialize?.handlingType === HandlingTypeEnum.CRATE_OF_WINE }, [
      Validators.required,
      Validators.min(1),
      BaryValidator.maxForPalletAndParcel(
        transportItemControls.handlingType,
        this.pricingTransporterConfiguration?.quotationItemsPalletMaxWidth,
        this.pricingTransporterConfiguration?.quotationItemsParcelMaxWidth
      ),
    ]);
    transportItemControls.groundPalletQuantity = new FormControl(serialize?.groundPalletQuantity, []);
    transportItemControls.otherSpecificQuantity = new FormControl(serialize?.otherSpecificQuantity, []);
    transportItemControls.description = new FormControl(serialize?.description);
    transportItemControls.lengthUnit = new FormControl({ value: serialize?.lengthUnit || MeasureUnitEnum.CENTIMETER, disabled: true });
    transportItemControls.weightUnit = new FormControl({ value: serialize?.weightUnit || MeasureUnitEnum.KG, disabled: true });
    transportItemControls.other = new FormControl(serialize?.other);
    transportItemControls.vehicleCategory = new FormControl(serialize?.vehicleCategory);
    transportItemControls.vehicleType = new FormControl(serialize?.vehicleType);
    return transportItemControls as TransportItemControls;
  }

  public makeLoginControls(): LoginControls {
    return {
      email: this.fb.control(''),
      password: this.fb.control('', [Validators.minLength(3), Validators.maxLength(50)]),
      rememberMe: this.fb.control(false, []),
    };
  }

  public makeSearchPricingControls(): SearchPricingControls {
    return {
      pickupAddress: new AddressFormBuilder(this, this.baryAddressUtil)
        .init({
          withoutFullname: true,
          withoutValidators: true,
        })
        .build(),
      deliveryAddress: new AddressFormBuilder(this, this.baryAddressUtil)
        .init({
          withoutFullname: true,
          withoutValidators: true,
        })
        .build(),
      quantity: this.fb.control('', [Validators.required]),
    };
  }

  public makeTransportControls(transporterId: number, transport: Transport | undefined): TransportControls {
    const transportControls = Object.create({});
    transportControls.itemsNature = this.fb.control(transport?.itemsNature ?? undefined, [Validators.required]);
    transportControls.goodsValue = this.fb.control(transport?.goodsValue ?? undefined, [
      Validators.min(0),
      Validators.max(this.pricingTransporterConfiguration?.quotationMaxGoodsValue ?? VALIDATOR_CONSTANTS.DEFAULT_HIGH_MAX_NUMBER),
    ]);
    transportControls.euroPallet = this.fb.control(transport?.euroPallet ?? undefined, []);
    transportControls.items = []; // ITransportItem[]
    transportControls.insuranceItem = this.fb.control(transport?.insuranceItem ?? undefined, []);
    transportControls.outsideEuDeliveryCountry = this.fb.control(transport?.outsideEuDeliveryCountry ?? undefined, []);
    transportControls.outsideEuDeliveryCity = this.fb.control(transport?.outsideEuDeliveryCity ?? undefined, []);
    transportControls.outsideEuDeliveryComments = this.fb.control(transport?.outsideEuDeliveryComments ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    transportControls.itemsCondition = this.fb.control(transport?.itemsCondition ?? ItemCondition.NEW, []);

    // PICKUP
    transportControls.pickupAddress = new AddressFormBuilder(this, this.baryAddressUtil).initFromTransport(transport, true).build();
    transportControls.pickupContact = new GenericBaryFormGroup<ContactControls>('', this.makePickupContactControls(transport), []);
    transportControls.pickupReference = this.fb.control(transport?.pickupReference ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    transportControls.pickupDate = this.fb.control(transport?.pickupDate ?? undefined, []);
    transportControls.pickupLoadingConstraint = this.fb.control(transport?.pickupLoadingConstraint ?? undefined, [Validators.required]);
    transportControls.pickupAccessConstraint = this.fb.control(
      transport?.pickupAccessConstraint ?? undefined,
      transport?.serviceType?.code === ServiceTypeEnum.FTL ? [] : [Validators.required]
    );
    transportControls.pickupMeeting = this.fb.control(transport?.pickupMeeting ?? undefined, []);
    transportControls.pickupComments = this.fb.control(transport?.pickupComments ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    transportControls.pickupTimeslotStart = this.fb.control(transport?.pickupTimeslotStart ?? undefined, []);
    transportControls.pickupTimeslotEnd = this.fb.control(transport?.pickupTimeslotEnd ?? undefined, []);
    transportControls.pickupRegisteredAddressId = this.fb.control(transport?.pickupRegisteredAddressId ?? undefined, []);
    transportControls.pickupPhoneCall = this.fb.control(transport?.pickupPhoneCall ?? undefined, []);
    transportControls.pickupFloor = this.fb.control(transport?.pickupFloor ?? undefined, []);
    transportControls.pickupAddressGpsCoords = this.fb.control(transport?.pickupAddressGpsCoords ?? undefined, []);

    // DELIVERY
    transportControls.deliveryAddress = new AddressFormBuilder(this, this.baryAddressUtil).initFromTransport(transport, false).build();
    transportControls.deliveryContact = new GenericBaryFormGroup<ContactControls>('', this.makeDeliveryContactControls(transport), []);
    transportControls.deliveryReference = this.fb.control(transport?.deliveryReference ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    transportControls.deliveryDate = this.fb.control(transport?.deliveryDate ?? undefined, []);
    transportControls.deliveryLoadingConstraint = this.fb.control(transport?.deliveryLoadingConstraint, [Validators.required]);
    transportControls.deliveryAccessConstraint = this.fb.control(
      transport?.deliveryAccessConstraint,
      transport?.serviceType?.code === ServiceTypeEnum.FTL ? [] : [Validators.required]
    );
    transportControls.deliveryMeeting = this.fb.control(transport?.deliveryMeeting ?? undefined, []);
    transportControls.deliveryComments = this.fb.control(transport?.deliveryComments ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    transportControls.deliveryTimeslotStart = this.fb.control(transport?.deliveryTimeslotStart ?? undefined, []);
    transportControls.deliveryTimeslotEnd = this.fb.control(transport?.deliveryTimeslotEnd ?? undefined, []);
    transportControls.deliveryRegisteredAddressId = this.fb.control(transport?.deliveryRegisteredAddressId ?? undefined, []);
    transportControls.deliveryPhoneCall = this.fb.control(transport?.deliveryPhoneCall ?? undefined, []);
    transportControls.deliveryFloor = this.fb.control(transport?.deliveryFloor ?? undefined, []);
    transportControls.deliveryAddressGpsCoords = this.fb.control(transport?.deliveryAddressGpsCoords ?? undefined, []);
    transportControls.quotationRejectionMessage = this.fb.control(transport?.quotationRejectionMessage ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]);

    transportControls.offerId = this.fb.control(transport?.offerId ?? undefined, []);

    if (transport?.transportExtraConditions && transport.transportExtraConditions.length > 0) {
      transportControls.transportExtraConditions = transport.transportExtraConditions.map(extra => this.makeTransportExtraConditionsForm(!!extra.adrCodeUN, extra));
    } else {
      transportControls.transportExtraConditions = [];
    }
    transportControls.transporter = new FormControl({ id: transporterId }, []);
    transportControls.merchandiseType = new FormControl(transport?.merchandiseType?.code || OfferMerchandiseType.CONDITIONED, [Validators.required]);
    transportControls.serviceType = new FormControl(transport?.serviceType?.code || ServiceTypeEnum.LTL, [Validators.required]);
    transportControls.isControlledTemperature = new FormControl(transport?.isControlledTemperature);
    transportControls.isHazardousMaterial = new FormControl(transport?.isHazardousMaterial);

    return transportControls as TransportControls;
  }

  makeNewShopControls(): NewShopControls {
    return {
      companyAddressCountry: new FormControl('FR', [Validators.required]),
      companyName: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      technicalName: new FormControl('', [Validators.required, Validators.pattern(/^[a-z-0-9]+$/)]),
      url: new FormControl('', [Validators.required, Validators.pattern(/^[a-z-0-9]+$/)]),
      primaryColor: new FormControl('#374473', [Validators.required]),
      secondaryColor: new FormControl('#3AAA35'),
      enableShop: new FormControl(true),
      forceEdiTestMode: new FormControl(true),
      fakeData: new FormControl(false),
      moderator: this.makeLoginForm(),
      domainName: new FormControl(undefined),
    } as NewShopControls;
  }

  public makeOfferControls(offer: OfferViewDTO): OfferControls {
    const offerControl = Object.create({}) as OfferControls;
    // DESCRIPTION
    offerControl.alias = this.fb.control(offer?.alias ?? '', [Validators.required, Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    offerControl.name = this.fb.control(offer?.name ?? '', [Validators.required, Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    offerControl.description = this.fb.control(offer?.description ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]);
    offerControl.status = this.fb.control(offer?.status ?? OfferStatus.INACTIVE, []);

    offerControl.measureUnit = this.fb.control(offer?.measureUnit ?? this.getDefaultMeasureUnitByOfferServiceType(offer), []);
    offerControl.weightingFactor = this.fb.control(offer?.weightingFactor ?? 0, []);
    offerControl.carbonEmissionFactor = this.fb.control(offer?.carbonEmissionFactor ?? null, [Validators.min(0)]);
    offerControl.flexibilityDeadline = this.fb.control(offer?.flexibilityDeadline ?? undefined, [Validators.min(1), Validators.max(7)]);

    const weightUnitValue = [PricingUnitEnum.PALLET, PricingUnitEnum.COL, PricingUnitEnum.KILOGRAM, PricingUnitEnum.LDM].includes(offerControl.measureUnit.value)
      ? LIST_PRICING_UNIT[offerControl.measureUnit.value]!.measureUnit!.value
      : null;

    offerControl.weightUnit = new FormControl({ value: weightUnitValue, disabled: true });
    offerControl.weightingFactor = this.fb.control(offer?.weightingFactor || null, offer?.weightingFactor ? [Validators.min(0)] : []);
    offerControl.toWeightWeighted = this.fb.control(!!offer?.weightingFactor, []);

    offerControl.itemScope = this.fb.control(offer?.itemScope ?? (offerControl.measureUnit.value === PricingUnitEnum.LDM ? PricingItemScope.RANGE : PricingItemScope.FIXED), []);
    offerControl.roundedUpPrice = this.fb.control(offer?.roundedUpPrice ?? false, []);
    offerControl.roundedPrice = this.fb.control(offer?.roundedPrice ?? false, []);

    // PICKUP ADDRESS
    offerControl.pickupAddressFloorNumber = this.fb.control(offer?.pickupAddressFloorNumber ?? false, []);
    offerControl.pickupComments = this.fb.control(offer?.pickupComments ?? false, []);
    offerControl.pickupPhoneCallAnHourBefore = this.fb.control(offer?.pickupPhoneCallAnHourBefore ?? false, []);
    offerControl.pickupFloorUpstairQuotationNeeded = this.fb.control(offer?.pickupFloorUpstairQuotationNeeded ?? false, []);
    offerControl.pickupVatExoneration = this.fb.control(offer?.pickupVatExoneration ?? false, []);
    offerControl.pickupTimeslotStart = this.fb.control(offer?.pickupTimeslotStart ?? 8, []);
    offerControl.pickupTimeslotStartDefault = this.fb.control(offer?.pickupTimeslotStartDefault ?? 8, []);
    offerControl.pickupTimeslotEnd = this.fb.control(offer?.pickupTimeslotEnd ?? 18, []);
    offerControl.pickupTimeslotEndDefault = this.fb.control(offer?.pickupTimeslotEndDefault ?? 18, []);
    offerControl.pickupTimeslotRange = this.fb.control(offer?.pickupTimeslotRange ?? 4, []);

    // DELIVERY ADDRESS
    offerControl.deliveryAddressFloorNumber = this.fb.control(offer?.deliveryAddressFloorNumber ?? false, []);
    offerControl.deliveryComments = this.fb.control(offer?.deliveryComments ?? false, []);
    offerControl.deliveryPhoneCallAnHourBefore = this.fb.control(offer?.deliveryPhoneCallAnHourBefore ?? false, []);
    offerControl.deliveryFloorUpstairQuotationNeeded = this.fb.control(offer?.deliveryFloorUpstairQuotationNeeded ?? false, []);
    offerControl.deliveryVatExoneration = this.fb.control(offer?.deliveryVatExoneration ?? false, []);
    offerControl.deliveryTimeslotStart = this.fb.control(offer?.deliveryTimeslotStart ?? 8, []);
    offerControl.deliveryTimeslotStartDefault = this.fb.control(offer?.deliveryTimeslotStartDefault ?? 8, []);
    offerControl.deliveryTimeslotEnd = this.fb.control(offer?.deliveryTimeslotEnd ?? 18, []);
    offerControl.deliveryTimeslotEndDefault = this.fb.control(offer?.deliveryTimeslotEndDefault ?? 18, []);
    offerControl.deliveryTimeslotRange = this.fb.control(offer?.deliveryTimeslotRange ?? 4, []);
    offerControl.productCode = this.fb.control(offer?.productCode ?? '', [Validators.minLength(1), Validators.maxLength(3)]);

    offerControl.offerDescriptionItems = this.fb.array((offer.offerDescriptionItems || []).map((odi: any) => this.fb.group(this.makeOfferDescriptionItemControls(odi))));

    offerControl.pricingOptions = this.fb.array((offer.pricingOptions || []).map((odi: any) => this.fb.group(this.makePricingOptionControls(odi))));

    offerControl.activatedTax = this.fb.control(offer?.activatedTax ?? false);

    offerControl.isControlledTemperature = this.fb.control(offer?.isControlledTemperature ?? false);
    offerControl.isHazardousMaterial = this.fb.control(offer?.isHazardousMaterial ?? false);

    if (offerControl.isControlledTemperature.value) {
      offerControl.minTemperatureThreshold = this.fb.control(offer?.minTemperatureThreshold ?? 0, [Validators.required, Validators.min(-40), Validators.max(60)]);
      offerControl.maxTemperatureThreshold = this.fb.control(offer?.maxTemperatureThreshold ?? 0, [Validators.required, Validators.min(-40), Validators.max(60)]);
    }

    return offerControl;
  }

  private getDefaultMeasureUnitByOfferServiceType(offer?: OfferViewDTO): PricingUnitEnum {
    return offer?.serviceType === ServiceTypeEnum.FTL ? PricingUnitEnum.KILOGRAM : PricingUnitEnum.PALLET;
  }

  makeOfferHandlingTypeControls(offerHandlingType?: IOfferHandlingType): OfferHandlingTypeControls {
    return {
      id: this.fb.control(offerHandlingType?.id),
      activated: this.fb.control(offerHandlingType?.activated, [Validators.required]),
      type: this.fb.control(offerHandlingType?.type, [Validators.required]),
      minWidth: this.fb.control(offerHandlingType?.minWidth, [Validators.min(1)]),
      minLength: this.fb.control(offerHandlingType?.minLength, [Validators.min(1)]),
      minHeight: this.fb.control(offerHandlingType?.minHeight, [Validators.min(1)]),
      maxWidth: this.fb.control(offerHandlingType?.maxWidth, [Validators.required, Validators.min(1)]),
      maxLength: this.fb.control(offerHandlingType?.maxLength, [Validators.required, Validators.min(1)]),
      maxHeight: this.fb.control(offerHandlingType?.maxHeight, [Validators.required, Validators.min(1)]),
      maxVolume: this.fb.control(offerHandlingType?.maxVolume, [Validators.min(0)]),
      minQuantity: this.fb.control(offerHandlingType?.minQuantity, [Validators.required, Validators.min(0)]),
      maxQuantity: this.fb.control(offerHandlingType?.maxQuantity, [Validators.required, Validators.min(1)]),
      minOtherQuantity: this.fb.control(offerHandlingType?.minOtherQuantity, [Validators.min(0)]),
      maxOtherQuantity: this.fb.control(offerHandlingType?.maxOtherQuantity, [Validators.min(1)]),
      minWeight: this.fb.control(offerHandlingType?.minWeight, [Validators.required, Validators.min(0)]),
      maxWeight: this.fb.control(offerHandlingType?.maxWeight, [Validators.required, Validators.min(1)]),
      maxWeightWithTailgate: this.fb.control(offerHandlingType?.maxWeightWithTailgate, [Validators.required, Validators.min(1)]),
      offer: this.fb.control(offerHandlingType?.offer, [Validators.required]),
    };
  }

  makeContactControls(contact?: IContact): ContactControls {
    return {
      civility: this.fb.control(contact?.civility ?? CIVILITIES_OPTIONS[0].value, []),
      firstName: this.fb.control(contact?.firstName ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      lastName: this.fb.control(contact?.lastName ?? '', [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      mail: this.fb.control(contact?.mail ?? '', [Validators.email]),
      phone: this.fb.control(contact?.phone ?? '', [PhoneValidator]),
    };
  }

  makeRegisteredAddressControls(registeredAddress?: IRegisteredAddress): RegisteredAddressControls {
    let addressType = AddressTypeEnum.PICKUP;
    if (registeredAddress) {
      addressType = RegisteredAddressMapper.getAddressType(registeredAddress);
    }

    const addressOption = {
      address: registeredAddress ? RegisteredAddressMapper.getAddress(registeredAddress) : undefined,
    };

    return {
      alias: this.fb.control(registeredAddress?.alias ?? '', [Validators.required]),
      addressType: this.fb.control(addressType, []),
      contact: this.makeContactForm(registeredAddress ? RegisteredAddressMapper.getContact(registeredAddress) : undefined),
      address: this.makeAddressForm(addressOption),
      accessConstraint: this.fb.control(registeredAddress?.accessConstraint ?? undefined, [Validators.required]),
      loadingConstraint: this.fb.control(registeredAddress?.loadingConstraint ?? undefined, [Validators.required]),
      cityCode: this.fb.control(registeredAddress?.cityCode ?? undefined),
    };
  }

  makeOfferDescriptionItemControls(model?: IOfferDescriptionItem): OfferDescriptionItemControls {
    return {
      label: this.fb.control(model?.label ?? '', []),
      icon: this.fb.control(model?.icon ?? '', []),
      iconColor: this.fb.control(model?.iconColor ?? '', []),
    };
  }

  makePricingOptionControls(model?: IPricingOption): PricingOptionControls {
    return {
      id: this.fb.control(model?.id ?? undefined, []),
      nameKey: this.fb.control(model?.nameKey ?? '', []),
      name: this.fb.control(model?.name ?? '', []),
      value: this.fb.control(model?.value ?? undefined, []),
      quotationNeeded: this.fb.control(model?.quotationNeeded ?? false, []),
      calculationMethod: this.fb.control(model?.calculationMethod ?? CalculationMethod.ADD_FIXED, []),
      type: this.fb.control(model?.type ?? undefined, []),
      step: this.fb.control(model?.step ?? TransportStep.PICKUP, []),
    };
  }

  makeInsuranceItemControls(insuranceItems?: InsuranceItem): InsuranceItemControls {
    return {
      id: this.fb.control(insuranceItems?.id ?? undefined, []),
      basePricing: this.fb.control(insuranceItems?.basePricing ?? undefined, []),
      itemsNature: this.fb.control(insuranceItems?.itemsNature ?? undefined, []),
      position: this.fb.control(insuranceItems?.position ?? undefined, []),
      maxValue: this.fb.control(insuranceItems?.maxValue ?? undefined, [Validators.max(5000000)]),
      insuranceRate: this.fb.control(insuranceItems?.insuranceRate ?? undefined, [Validators.min(0), Validators.max(1)]),
      pricingRate: this.fb.control(insuranceItems?.pricingRate ?? undefined, [Validators.min(0), Validators.max(1)]),
      quotationRequired: this.fb.control(insuranceItems?.quotationRequired ?? false, []),
      offer: this.fb.control(insuranceItems?.offer ?? undefined, []),
    };
  }

  makeMerchandiseControls(merchandise?: Merchandise): MerchandiseControls {
    return {
      id: this.fb.control(merchandise?.id ?? undefined, []),
      position: this.fb.control(merchandise?.position ?? undefined, []),
      forceQuotation: this.fb.control(merchandise?.forceQuotation ?? false, []),
      newName: this.fb.control(merchandise?.newName ?? undefined, [Validators.required]),
      offers: this.fb.control(merchandise?.offers ?? undefined, []),
    };
  }

  makeMiddleCustomerControls(customer?: ICustomer): MiddleCustomerControls {
    return {
      civility: this.fb.control(customer?.user.civility ?? undefined, []),
      firstName: this.fb.control(customer?.user.firstName ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      lastName: this.fb.control(customer?.user.lastName ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      phone: this.fb.control(customer?.user.phone ?? undefined, [PhoneValidator]),
      address: this.makeMiddleCustomerAddressControls(customer!),
      email: this.fb.control(customer?.user.email ?? undefined, []),
      billEmail: this.fb.control(customer?.user.billEmail ?? undefined, [Validators.email]),
      accountingCode: this.fb.control(customer?.accountingCode ?? undefined),
      transporterAlias: this.fb.control(customer?.transporterAlias ?? undefined),
    };
  }

  private makeMiddleCustomerAddressControls(customer: ICustomer): FormControl {
    const address: string =
      customer.user.billAddressStreetNumber + ' ' + customer.user.billAddressStreet + ', ' + customer.user.billAddressZipCode + ' ' + customer.user.billAddressCity;
    return this.fb.control(address, []);
  }

  makeMiddleCommentControls(customer?: ICustomer): MiddleCommentControls {
    return {
      comment: this.fb.control(customer?.commentTransporter ?? undefined, [Validators.pattern(StringUtil.ALLOWED_CHARS)]),
    };
  }

  makeMiddleUserForcedActivationControls(customer?: ICustomer): MiddleUserForcedActivationControls {
    return {
      userForcedActivation: this.fb.control(customer?.user.activated ?? undefined, []),
    };
  }

  makeHeadbandControls(headband?: IHeadband): HeadbandControls {
    return {
      id: this.fb.control(headband?.id ?? undefined, []),
      type: this.fb.control(headband?.type ?? undefined, [Validators.required, Validators.pattern(StringUtil.ALLOWED_CHARS)]),
      endDate: this.fb.control(headband?.endDate ?? undefined, [Validators.required]),
      startDate: this.fb.control(headband?.startDate ?? undefined, [Validators.required]),
      content: this.fb.control(headband?.content ?? undefined, [Validators.required, Validators.pattern(StringUtil.ALLOWED_CHARS), Validators.maxLength(170)]),
      transporters: this.fb.control(headband?.transporters ?? undefined, [Validators.required]),
      transporterIds: this.fb.control(headband?.transporterIds ?? undefined),
    };
  }

  makeTransportExtraConditionsControls(isAdr = false, transportExtraCondition: ITransportExtraCondition | undefined): TransportExtraConditionControls {
    const transportExtraConditionCtrs = {
      id: this.fb.control(transportExtraCondition?.id ?? undefined, []),
      adrIsLimitedQuantity: this.fb.control(transportExtraCondition?.adrIsLimitedQuantity ?? undefined, [Validators.required]),
      adrCodeUN: this.fb.control(transportExtraCondition?.adrCodeUN ?? undefined, [Validators.maxLength(4)]),
      adrDescription: this.fb.control(transportExtraCondition?.adrDescription ?? undefined, []),
      adrPacking: this.fb.control(transportExtraCondition?.adrPacking ?? undefined, []),
      adrPackingNumber: this.fb.control(transportExtraCondition?.adrPackingNumber ?? undefined, []),
      adrPackingGroup: this.fb.control(transportExtraCondition?.adrPackingGroup ?? undefined, []),
      adrTransportCategory: this.fb.control(transportExtraCondition?.adrTransportCategory ?? undefined, []),
      adrNetWeight: this.fb.control(transportExtraCondition?.adrNetWeight ?? undefined, []),
      adrIsDangerousForEnvironment: this.fb.control(transportExtraCondition?.adrIsDangerousForEnvironment ?? undefined, []),
      adrIsWaste: this.fb.control(transportExtraCondition?.adrIsWaste ?? undefined, []),
      adrWasteBsdCode: this.fb.control(transportExtraCondition?.adrWasteBsdCode ?? undefined, [Validators.pattern('BSD-\\d{8}-.{9}')]),
      controlledTemperature: this.fb.control(transportExtraCondition?.controlledTemperature ?? undefined, []),
    };

    if (isAdr) {
      transportExtraConditionCtrs.adrCodeUN.addValidators(Validators.required);
      transportExtraConditionCtrs.adrDescription.addValidators(Validators.required);
      transportExtraConditionCtrs.adrPackingGroup.addValidators(Validators.required);
      transportExtraConditionCtrs.adrNetWeight.addValidators(Validators.required);
    }

    return transportExtraConditionCtrs;
  }

  makeThemeControls(themeView?: ThemeView): ThemeControls {
    const themeControls = Object.create({}) as ThemeControls;
    themeControls.id = this.fb.control(themeView?.id ?? undefined, []);
    themeControls.activated = this.fb.control(themeView?.activated ?? undefined, Validators.required);
    themeControls.transporter = this.fb.control(themeView?.transporter ?? undefined, Validators.required);
    return themeControls;
  }

  makeThemeDataControls(themeData?: ThemeData): ThemeDataControls {
    const themeDataControls = Object.create({}) as ThemeDataControls;

    themeDataControls.carouselItems = [];

    themeDataControls.faqs = [];

    themeDataControls.footerTransporterPhoneNumber = this.fb.control(themeData?.footerTransporterPhoneNumber ?? undefined, []);
    themeDataControls.footerTransporterAddress = this.fb.control(themeData?.footerTransporterAddress ?? undefined, []);
    themeDataControls.footerTransporterZipcode = this.fb.control(themeData?.footerTransporterZipcode ?? undefined, []);
    themeDataControls.footerTransporterCity = this.fb.control(themeData?.footerTransporterCity ?? undefined, []);
    themeDataControls.footerContactButtonAction = this.fb.control(themeData?.footerContactButtonAction ?? undefined, []);
    themeDataControls.footerTransporterDescription = this.fb.control(themeData?.footerTransporterDescription ?? undefined, []);
    themeDataControls.footerTransporterGroup = this.fb.control(themeData?.footerTransporterGroup ?? undefined, []);

    themeDataControls.mainJumbotronTitle = this.fb.control(themeData?.mainJumbotronTitle ?? undefined, []);
    themeDataControls.mainJumbotronSubtitle = this.fb.control(themeData?.mainJumbotronSubtitle ?? undefined, []);
    themeDataControls.mainJumbotronImage = this.fb.control(themeData?.mainJumbotronImage ?? undefined, []);
    themeDataControls.mainJumbotronContent = this.fb.control(themeData?.mainJumbotronContent ?? undefined, []);
    themeDataControls.mainSplashMessage = this.fb.control(themeData?.mainSplashMessage ?? undefined, []);

    themeDataControls.splashLogoPreview = this.fb.control(null, []);
    themeDataControls.splashLogo = this.fb.control(themeData?.splashLogoPreview ?? undefined, []);
    themeDataControls.mainLogoPreview = this.fb.control(null, []);
    themeDataControls.mainLogo = this.fb.control(themeData?.mainLogoPreview ?? undefined, []);
    themeDataControls.disconnectedLogoPreview = this.fb.control(null, []);
    themeDataControls.disconnectedLogo = this.fb.control(themeData?.disconnectedLogoPreview ?? undefined, []);

    return themeDataControls;
  }

  makeCarouselItemControls(carouselItem?: CarouselItem): CarouselItemControls {
    const carouselItemControls = Object.create({});
    carouselItemControls.title = this.fb.control(carouselItem?.title, [Validators.required]);
    carouselItemControls.description = this.fb.control(carouselItem?.description, [Validators.required]);
    carouselItemControls.image = this.fb.control(carouselItem?.image, undefined);
    return carouselItemControls as CarouselItemControls;
  }

  makeFaqItemControls(faqItem: FaqItem): FaqItemControls {
    const faqItemControls = Object.create({});
    faqItemControls.question = this.fb.control(faqItem.question, [Validators.required]);
    faqItemControls.answer = this.fb.control(faqItem.answer, [Validators.required]);

    return faqItemControls as FaqItemControls;
  }
}
